import PropTypes from "prop-types"
import React, { useCallback, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import JobLineItemForm from "src/main/Service/Manage/shared/JobLineItemForm"

import DiscardConfirmModal from "src/components/DiscardConfirmModal"
import Modal from "src/components/Modal"

import { updateJobLineItem } from "src/api/Service/JobLineItems"

import { useDiscardConfirm } from "src/hooks/use_discard_confirm"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import {
  EDIT_LINE_ITEM_DISCARDED,
  EDIT_LINE_ITEM_SAVED,
  EDIT_LINE_ITEM_SAVE_FAILED,
} from "./amplitude_events"

const availabilityForLineItem = (lineItem) =>
  lineItem?.productType === "labor" ? "service_labor" : "service_parts"

const EditJobLineItemModal = ({
  isOpen,
  onClose,
  jobId,
  lineItem,
  boatLengthFeet,
  initialFocus = "rate",
}) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const tracker = useTracker()

  const [isDirty, setIsDirty] = useState(false)

  const { handleCloseRequest, discardConfirmProps } = useDiscardConfirm({
    isDirty,
    onClose,
  })

  const handleDiscard = useCallback(() => {
    tracker.trackEvent(EDIT_LINE_ITEM_DISCARDED, {
      lineItemId: lineItem?.id,
    })
    discardConfirmProps.onDiscard()
  }, [tracker, lineItem?.id, discardConfirmProps])

  const { mutate: submit, isLoading } = useMutation(
    (vars) => updateJobLineItem(marinaSlug, jobId, lineItem?.id, vars),
    {
      onSuccess: (_data, vars) => {
        const changedFields = []
        if (vars.pricePerUnit !== lineItem?.pricePerUnit)
          changedFields.push("price_per_unit")
        if (vars.quantity !== Number(lineItem?.quantity))
          changedFields.push("quantity")
        if ((vars.notes ?? "") !== (lineItem?.notes ?? ""))
          changedFields.push("notes")

        tracker.trackEvent(EDIT_LINE_ITEM_SAVED, {
          lineItemId: lineItem?.id,
          productId: lineItem?.product?.id,
          changedFields,
        })

        queryClient.invalidateQueries(["jobLineItems", marinaSlug, jobId])
        showToast("Line item updated", { type: "success" })
        onClose()
      },
      onError: (error) => {
        tracker.trackEvent(EDIT_LINE_ITEM_SAVE_FAILED, {
          lineItemId: lineItem?.id,
          message: error.message,
        })
        showToast(error.message || "Failed to update line item.", {
          type: "error",
        })
      },
    }
  )

  const handleFormSubmit = ({ pricePerUnit, quantity, notes }) => {
    submit({ pricePerUnit, quantity, notes })
  }

  if (!lineItem) return null

  const initialValues = {
    product: lineItem.product,
    productId: lineItem.product?.id,
    pricePerUnit: lineItem.pricePerUnit,
    quantity: lineItem.quantity,
    notes: lineItem.notes ?? "",
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCloseRequest} size="default">
        <Modal.Header title="Edit Item" />
        <Modal.Body>
          <JobLineItemForm
            availability={availabilityForLineItem(lineItem)}
            boatLengthFeet={boatLengthFeet}
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
            onCancel={handleCloseRequest}
            submitLabel={isLoading ? "Saving…" : "Save"}
            lockProduct
            initialFocus={initialFocus}
            onDirtyChange={setIsDirty}
          />
        </Modal.Body>
      </Modal>

      <DiscardConfirmModal {...discardConfirmProps} onDiscard={handleDiscard} />
    </>
  )
}

EditJobLineItemModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  lineItem: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    pricePerUnit: PropTypes.number.isRequired,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    notes: PropTypes.string,
    productType: PropTypes.string,
    product: PropTypes.object,
  }),
  boatLengthFeet: PropTypes.number,
  initialFocus: PropTypes.oneOf(["product", "rate", "notes"]),
}

export default EditJobLineItemModal
